import React, { CSSProperties, useState } from 'react';
import {
    ListItem,
    ListItemText,
    Box,
    Typography,
    lighten,
    useTheme,
    Avatar,
    Tooltip,
} from '@material-ui/core';
import { alpha } from '@material-ui/core/styles';
import { BellOff, FileText, Image, Mic, UserCheck, Video } from 'react-feather';
import moment from 'moment';
import { ChatbotIcon } from 'src/components/ChatbotIcon';
import getInitials from 'src/utils/getInitials';
import { MEDIA_CONFIG } from 'src/modules/conversations/constants/media';
import { getIntensityColor } from '../../utils';
import { ThreadTagsLabel, ThreadTagsBadge } from '../Tags';
import { useTranslation } from 'react-i18next';
import { formatDateLabel } from '../../utils/formatDateLabel';

export const ThreadItem = ({
    thread,
    tagsView,
    active,
    onClick,
}: {
    thread: any;
    tagsView: 'none' | 'labels' | 'badges';
    active?: boolean;
    onClick?: () => void;
}) => {
    const theme = useTheme();
    const [hover, setHover] = useState(false);
    const { t } = useTranslation();
    const {
        name,
        lastMessage,
        isSilenced,
        isUsingChatBot,
        hasLead,
        storeName,
        tags,
        isRead,
        channel,
    } = thread;
    const { message, mediaUrl, createdAt, origin } = lastMessage || {};
    const isUnread = !isRead && origin === 'customer';

    const isConversationActive = Boolean(active);

    const intensityColor = getIntensityColor(theme.palette.type);

    const mainColor = isConversationActive
        ? lighten(theme.palette.primary.main, intensityColor)
        : isUnread
          ? lighten(theme.palette.primary.main, 0.9)
          : theme.palette.background.paper;

    const mainTextColor = theme.palette.getContrastText(mainColor);
    const mutedTextColor = isConversationActive
        ? mainTextColor
        : theme.palette.text.secondary;
    const unreadLabelColor = alpha(theme.palette.primary.main, 0.82);

    const backgroundColor = hover
        ? isConversationActive
            ? lighten(mainColor, intensityColor)
            : theme.palette.action.hover
        : mainColor;

    const truncateStyle: CSSProperties = {
        display: '-webkit-box',
        WebkitLineClamp: 1,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
    };

    const getMediaIcon = (url: string) => {
        const ext = url.split('.').pop();

        const iconStyle = { color: mainTextColor };

        if (MEDIA_CONFIG.video.extensions.includes(ext))
            return (
                <Box>
                    <Video size={20} style={iconStyle} />
                </Box>
            );

        if (MEDIA_CONFIG.audio.extensions.includes(ext))
            return (
                <Box
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                    }}
                >
                    <Mic size={20} style={iconStyle} />
                    <Typography
                        style={{
                            ...truncateStyle,
                            color: mainTextColor,
                            flexGrow: 1,
                        }}
                    >
                        Te ha enviado un audio
                    </Typography>
                </Box>
            );

        if (MEDIA_CONFIG.document.extensions.includes(ext))
            return (
                <Box>
                    <FileText size={20} style={iconStyle} />
                </Box>
            );

        return (
            <Box>
                <Image size={20} style={iconStyle} />
            </Box>
        );
    };

    const createdAtMoment = moment(createdAt);
    const dateLabel = createdAtMoment.isSame(moment(), 'day')
        ? createdAtMoment.fromNow()
        : createdAtMoment.isSame(moment().subtract(1, 'day'), 'day')
          ? t('Chat.DateYesterday')
          : formatDateLabel(new Date(createdAt), t);

    const CHANNEL_ICONS = {
        whatsapp: '/static/logos/whatsapp.png',
        messenger: '/static/logos/messenger.png',
    };
    const channelIcon = CHANNEL_ICONS[channel] || CHANNEL_ICONS.whatsapp;

    return (
        <ListItem
            button
            style={{
                backgroundColor,
                transition: '0.4s',
                display: 'flex',
                flexDirection: 'column',
                gap: 5,
            }}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            onClick={onClick}
        >
            {tagsView === 'badges' && <ThreadTagsBadge tags={tags || []} />}
            <Box
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 15,
                    width: '100%',
                }}
            >
                <Avatar src={channelIcon} style={{ width: 40, height: 40 }}>
                    {getInitials(name)}
                </Avatar>

                <ListItemText
                    primary={
                        <Box
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 5,
                            }}
                        >
                            <Box
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    gap: 5,
                                }}
                            >
                                {storeName && (
                                    <Typography
                                        variant="caption"
                                        style={{
                                            ...truncateStyle,
                                            color: mutedTextColor,
                                            fontSize: 12,
                                            fontWeight: isUnread ? 700 : 500,
                                            lineHeight: 1.2,
                                            opacity: isConversationActive
                                                ? 0.82
                                                : 1,
                                        }}
                                    >
                                        {storeName}
                                    </Typography>
                                )}

                                {hasLead && (
                                    <Tooltip title={t('Chat.LeadAssigned')}>
                                        <UserCheck
                                            size={16}
                                            color={theme.palette.primary.main}
                                        />
                                    </Tooltip>
                                )}
                                {isSilenced && (
                                    <Tooltip
                                        title={t('Chat.ConversationMuted')}
                                    >
                                        <BellOff
                                            size={16}
                                            color={theme.palette.primary.main}
                                        />
                                    </Tooltip>
                                )}
                                {isUsingChatBot && (
                                    <Tooltip title={t('Chat.ChatBotActive')}>
                                        <ChatbotIcon
                                            style={{
                                                fontSize: 16,
                                                color: theme.palette.primary
                                                    .main,
                                            }}
                                        />
                                    </Tooltip>
                                )}
                            </Box>

                            <Typography
                                variant="body1"
                                style={{
                                    ...truncateStyle,
                                    color:
                                        isUnread && !isConversationActive
                                            ? unreadLabelColor
                                            : mainTextColor,
                                    fontWeight: isUnread ? 700 : 500,
                                    opacity:
                                        isUnread && !isConversationActive
                                            ? 0.9
                                            : 1,
                                    letterSpacing: 0.5,
                                    flexGrow: 1,
                                    minWidth: 0,
                                }}
                            >
                                {name}
                            </Typography>
                        </Box>
                    }
                    secondary={
                        <Box
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                minWidth: 0,
                            }}
                        >
                            {mediaUrl && getMediaIcon(mediaUrl)}

                            {message && (
                                <Typography
                                    style={{
                                        ...truncateStyle,
                                        color: isUnread
                                            ? mainTextColor
                                            : mutedTextColor,
                                        flexGrow: 1,
                                        fontSize: 13,
                                        fontWeight: isUnread ? 700 : 400,
                                        lineHeight: 1.35,
                                        opacity: isConversationActive
                                            ? 0.82
                                            : 1,
                                    }}
                                >
                                    {message}
                                </Typography>
                            )}

                            <Typography
                                variant="caption"
                                style={{
                                    color: mutedTextColor,
                                    fontSize: 11,
                                    fontWeight: isUnread ? 700 : 400,
                                    lineHeight: 1.2,
                                    opacity: isConversationActive ? 0.75 : 1,
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {dateLabel}
                            </Typography>
                        </Box>
                    }
                />
            </Box>
            {tagsView === 'labels' && <ThreadTagsLabel tags={tags || []} />}
        </ListItem>
    );
};
